// src/pages/CreateAccountPage.js
import React, { useContext, useState } from "react";
import { LanguageContext } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../services/api";

function CreateAccountPage() {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    address: "",
  });

  const TEXT = {
    EN: {
      title: "Create Account",
      back: "×",
      create: "Create Account",
      required: "All fields are required except email.",
      already: "Already have an account?",
      login: "Login",
    },
    AR: {
      title: "إنشاء حساب",
      back: "×",
      create: "إنشاء حساب",
      required: "كل الحقول مطلوبة ما عدا البريد الإلكتروني.",
      already: "لديك حساب بالفعل؟",
      login: "تسجيل الدخول",
    },
  };

  const t = TEXT[lang];

  // 📌 AUTO-FORMAT PHONE NUMBER (same logic as checkout)
const formatPhone = (val) => {
  // allow international numbers untouched (except +961)
  if (val.startsWith("+") && !val.startsWith("+961")) {
    return val;
  }

  let digits = val.replace(/\D/g, "");

  // handle +961
  if (val.startsWith("+961")) {
    digits = "961" + digits.slice(3);
  }

  if (digits.startsWith("961")) {
    const rest = digits.slice(3);
    return `+961 ${rest.slice(0, 2)} ${rest.slice(2, 5)} ${rest.slice(5, 8)}`.trim();
  }

  // local numbers (0,7,8)
  if (/^[078]/.test(digits)) {
    if (digits.length <= 2) return digits;
    if (digits.length <= 5)
      return `${digits.slice(0, 2)} ${digits.slice(2)}`;
    return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)}`;
  }

  // fallback (other international formats)
  return val;
};


  const handleChange = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async () => {
  if (!form.name || !form.phone || !form.city || !form.address) {
    alert(lang === "EN" ? "Please fill all required fields." : "يرجى ملء جميع الحقول المطلوبة");
    return;
  }

  try {
    // 1️⃣ Create guest user + token
    const auth = await apiRequest("/auth/guest", {
      method: "POST",
      body: JSON.stringify({
        full_name: form.name,
        phone: form.phone,
        email: form.email || null,
      }),
    });

    // 2️⃣ Save token
    localStorage.setItem("esill-token", auth.token);

    // 🔥 MERGE GUEST CART INTO USER CART
const sessionToken = localStorage.getItem("esill-session");
if (sessionToken) {
  try {
    await apiRequest("/cart/merge", {
      method: "POST",
      body: JSON.stringify({ session_token: sessionToken }),
    });

    // remove guest session after merge
    localStorage.removeItem("esill-session");

    // notify CartContext
    window.dispatchEvent(new Event("auth-changed"));
  } catch (e) {
    console.warn("Cart merge failed", e);
  }
}


    // 3️⃣ Save address using existing users/me endpoint
    const user = await apiRequest("/users/me", {
      method: "PUT",
      body: JSON.stringify({
        full_name: form.name,
        phone: form.phone,
        email: form.email || null,
        city: form.city,
        area: form.address, // simple mapping
        street: "",
        building: "",
      }),
    });

    localStorage.setItem("esill-user", JSON.stringify(user));

    // 4️⃣ Go to account
    navigate("/account");
  } catch (err) {
    alert(err.message);
  }
};


  return (
    <div
      className="w-full min-h-screen bg-white pt-[95px] px-5 pb-24"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-[80px] left-4 text-3xl opacity-60 hover:opacity-100"
      >
        {t.back}
      </button>

      {/* Title */}
      <h1 className="text-xl font-semibold text-center">{t.title}</h1>
      <div className="h-[2px] w-20 bg-black mx-auto mt-2 mb-6"></div>

      <p className="text-center text-xs text-gray-500 mb-4">{t.required}</p>

      {/* FORM */}
      <div className="flex flex-col gap-4 max-w-xl mx-auto">
        <input
          placeholder={lang === "EN" ? "Full Name" : "الاسم الكامل"}
          className="checkout-input"
          value={form.name}
          onChange={(e) => handleChange("name", e.target.value)}
        />
        <input
  placeholder={lang === "EN" ? "Phone Number" : "رقم الهاتف"}
  className="checkout-input"
  value={form.phone}
  onChange={(e) => handleChange("phone", formatPhone(e.target.value))}
/>
        <input
          placeholder={lang === "EN" ? "Email (optional)" : "البريد الإلكتروني (اختياري)"}
          className="checkout-input"
          value={form.email}
          onChange={(e) => handleChange("email", e.target.value)}
        />
        <input
          placeholder={lang === "EN" ? "City" : "المدينة"}
          className="checkout-input"
          value={form.city}
          onChange={(e) => handleChange("city", e.target.value)}
        />
        <input
          placeholder={lang === "EN" ? "Address" : "العنوان"}
          className="checkout-input"
          value={form.address}
          onChange={(e) => handleChange("address", e.target.value)}
        />

        <button
          onClick={submit}
          className="mt-4 w-full py-3 bg-black text-white rounded-md text-lg font-semibold hover:bg-[#222]"
        >
          {t.create}
        </button>
        <button
  onClick={() => navigate("/contact")}
  className="w-full py-3 border border-black rounded-md text-sm font-semibold"
>
  {lang === "EN" ? "Support / Messages" : "الدعم والرسائل"}
</button>
        {/* LOGIN LINK */}
<div className="text-center mt-4 text-sm">
  <span className="text-gray-600">{t.already} </span>
  <button
    onClick={() => {
      localStorage.setItem("esill-open-login", "1");
      navigate("/");
    }}
    className="underline font-semibold hover:text-black"
  >
    {t.login}
  </button>
</div>
      </div>
    </div>
  );
}

export default CreateAccountPage;
